import React, { useState, useEffect } from 'react';
import { User, VehicleData, ConsultationProductType, ConsultaPlacaBasicaResult, ConsultaPlacaBasicaResponse, SolicitarRelatorioResult, SolicitarRelatorioResponse, SituacaoConsulta, SITUACAO_CONSULTA_INFO, TipoConsultaRelatorio, TIPO_CONSULTA_INFO, ConsultarRenainfResult, ConsultarRenainfResponse, ConsultarPrecoFipeResult, ConsultarPrecoFipeResponse, ConsultarProprietarioResult, ConsultarProprietarioResponse, ConsultarLeilaoPrimeResult, ConsultarLeilaoPrimeResponse, ConsultarSinistroPTResult, ConsultarSinistroPTResponse } from '../types';
import { Plus, Clock, ShieldCheck, AlertTriangle, Loader2, Coins, X, Car, Sparkles, Hash, FileText, RefreshCw, CheckCircle, Hourglass, FileDown, DollarSign, User as UserIcon, Gavel, AlertOctagon } from 'lucide-react';
import { getConsultationHistory, ConsultationRecord, getConsultationProductsList, consultarProtocolo } from '../services/consultationService';
import BuyCreditsModal from './BuyCreditsModal';
import ConsultaPlacaBasicaModal from './ConsultaPlacaBasicaModal';
import ConsultaChassiBasicaModal from './ConsultaChassiBasicaModal';
import SolicitarRelatorioModal from './SolicitarRelatorioModal';
import ConsultaRenainfModal from './ConsultaRenainfModal';
import ConsultaPrecoFipeModal from './ConsultaPrecoFipeModal';
import ConsultaProprietarioModal from './ConsultaProprietarioModal';
import ConsultaLeilaoPrimeModal from './ConsultaLeilaoPrimeModal';
import ConsultaSinistroPTModal from './ConsultaSinistroPTModal';

interface DashboardProps {
  user: User;
  onCreditsUpdate: (credits: number) => void;
  onShowResult: (data: VehicleData) => void;
  onShowConsultaBasicaResult: (data: ConsultaPlacaBasicaResult) => void;
  onShowRelatorioResult: (data: SolicitarRelatorioResult) => void;
  onShowRenainfResult: (data: ConsultarRenainfResult) => void;
  onShowFipeResult: (data: ConsultarPrecoFipeResult) => void;
  onShowProprietarioResult: (data: ConsultarProprietarioResult) => void;
  onShowLeilaoResult: (data: ConsultarLeilaoPrimeResult) => void;
  onShowSinistroPTResult: (data: ConsultarSinistroPTResult) => void;
  onNavigateToBuyCredits?: () => void;
}

const Dashboard: React.FC<DashboardProps> = ({ user, onCreditsUpdate, onShowResult, onShowConsultaBasicaResult, onShowRelatorioResult, onShowRenainfResult, onShowFipeResult, onShowProprietarioResult, onShowLeilaoResult, onShowSinistroPTResult, onNavigateToBuyCredits }) => {
  const [consultationProducts, setConsultationProducts] = useState<ConsultationProductType[]>([]);
  const [history, setHistory] = useState<ConsultationRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [showBuyCredits, setShowBuyCredits] = useState(false);
  const [selectedConsultationProduct, setSelectedConsultationProduct] = useState<ConsultationProductType | null>(null);
  const [insufficientCreditsMessage, setInsufficientCreditsMessage] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [historyData, productsList] = await Promise.all([
        getConsultationHistory(10),
        getConsultationProductsList(),
      ]);
      
      setHistory(historyData);
      setConsultationProducts(productsList);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConsultaBasicaSuccess = (result: ConsultaPlacaBasicaResponse) => {
    setSelectedConsultationProduct(null);
    onCreditsUpdate(result.remainingCredits);
    onShowConsultaBasicaResult(result.result);
    
    // Refresh history
    getConsultationHistory(10).then(setHistory);
  };

  const handleRelatorioSuccess = (result: SolicitarRelatorioResponse) => {
    setSelectedConsultationProduct(null);
    onCreditsUpdate(result.remainingCredits);
    onShowRelatorioResult(result.result);
    
    // Refresh history
    getConsultationHistory(10).then(setHistory);
  };

  const handleRenainfSuccess = (result: ConsultarRenainfResponse) => {
    setSelectedConsultationProduct(null);
    onCreditsUpdate(result.remainingCredits);
    onShowRenainfResult(result.result);
    
    // Refresh history
    getConsultationHistory(10).then(setHistory);
  };

  const handleFipeSuccess = (result: ConsultarPrecoFipeResponse) => {
    setSelectedConsultationProduct(null);
    onCreditsUpdate(result.remainingCredits);
    onShowFipeResult(result.result);
    
    // Refresh history
    getConsultationHistory(10).then(setHistory);
  };

  const handleProprietarioSuccess = (result: ConsultarProprietarioResponse) => {
    setSelectedConsultationProduct(null);
    onCreditsUpdate(result.remainingCredits);
    onShowProprietarioResult(result.result);
    
    // Refresh history
    getConsultationHistory(10).then(setHistory);
  };

  const handleLeilaoSuccess = (result: ConsultarLeilaoPrimeResponse) => {
    setSelectedConsultationProduct(null);
    onCreditsUpdate(result.remainingCredits);
    onShowLeilaoResult(result.result);
    
    // Refresh history
    getConsultationHistory(10).then(setHistory);
  };

  const handleSinistroPTSuccess = (result: ConsultarSinistroPTResponse) => {
    setSelectedConsultationProduct(null);
    onCreditsUpdate(result.remainingCredits);
    onShowSinistroPTResult(result.result);
    
    // Refresh history
    getConsultationHistory(10).then(setHistory);
  };

  const handleInsufficientCredits = () => {
    setSelectedConsultationProduct(null);
    setShowBuyCredits(true);
  };

  const handleConsultationProductClick = (product: ConsultationProductType) => {
    // Verificar se o usuário possui créditos suficientes
    if (user.credits < product.creditCost) {
      const creditsNeeded = product.creditCost - user.credits;
      setInsufficientCreditsMessage(
        `Você precisa de ${product.creditCost} créditos para esta consulta, mas possui apenas ${user.credits}. Faltam ${creditsNeeded} crédito${creditsNeeded > 1 ? 's' : ''}.`
      );
      // Navegar para a página de compra após 3 segundos
      setTimeout(() => {
        setInsufficientCreditsMessage(null);
        if (onNavigateToBuyCredits) {
          onNavigateToBuyCredits();
        } else {
          setShowBuyCredits(true);
        }
      }, 3000);
      return;
    }
    // Créditos suficientes, abrir modal de consulta
    setSelectedConsultationProduct(product);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8 space-y-8">
      
      {/* Insufficient Credits Toast */}
      {insufficientCreditsMessage && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 animate-in slide-in-from-top duration-300">
          <div className="bg-amber-900/95 border border-amber-500/50 rounded-xl p-4 shadow-2xl shadow-amber-500/20 max-w-md">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-amber-500/20 rounded-full flex items-center justify-center shrink-0">
                <Coins className="w-5 h-5 text-amber-400" />
              </div>
              <div className="flex-1">
                <h4 className="font-bold text-amber-100 mb-1">Créditos Insuficientes</h4>
                <p className="text-sm text-amber-200/80">{insufficientCreditsMessage}</p>
                <p className="text-xs text-amber-300/60 mt-2">
                  Redirecionando para compra de créditos...
                </p>
              </div>
              <button 
                onClick={() => {
                  setInsufficientCreditsMessage(null);
                  if (onNavigateToBuyCredits) {
                    onNavigateToBuyCredits();
                  } else {
                    setShowBuyCredits(true);
                  }
                }}
                className="p-1 hover:bg-amber-800/50 rounded-lg transition-colors text-amber-400 hover:text-amber-300"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            {/* Progress bar */}
            <div className="mt-3 h-1 bg-amber-950 rounded-full overflow-hidden">
              <div 
                className="h-full bg-amber-500 rounded-full" 
                style={{ 
                  width: '100%', 
                  animation: 'shrinkProgress 3s linear forwards' 
                }} 
              />
            </div>
            <style>{`
              @keyframes shrinkProgress {
                from { width: 100%; }
                to { width: 0%; }
              }
            `}</style>
          </div>
        </div>
      )}

      {/* Header Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-slate-900 p-6 rounded-xl border border-slate-800 flex items-center justify-between">
          <div>
            <p className="text-slate-400 text-sm">Saldo Disponível</p>
            <h3 className="text-3xl font-bold text-white">{user.credits} <span className="text-lg font-normal text-slate-500">créditos</span></h3>
          </div>
          <button 
            onClick={() => setShowBuyCredits(true)}
            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-1"
          >
            <Plus className="w-4 h-4" />
            Comprar
          </button>
        </div>
        
        <div className="bg-slate-900 p-6 rounded-xl border border-slate-800">
           <p className="text-slate-400 text-sm">Total Consultado</p>
           <h3 className="text-3xl font-bold text-white">{history.length} <span className="text-lg font-normal text-slate-500">veículos</span></h3>
        </div>

        <div className="bg-gradient-to-br from-indigo-900 to-slate-900 p-6 rounded-xl border border-indigo-500/30">
           <p className="text-indigo-200 text-sm">Plano Atual</p>
           <h3 className="text-xl font-bold text-white mb-1">Pré-pago</h3>
           <p className="text-xs text-indigo-300">Sem mensalidade. Pague pelo uso.</p>
        </div>
      </div>

      {/* Featured Consultation Products */}
      {consultationProducts.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-400" />
            <h2 className="text-xl font-bold text-white">Consultas Disponíveis</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {consultationProducts.map((product) => {
              // Define icon and colors based on product type
              const isChassisProduct = product.id === 'consulta_chassi_basica';
              const isRelatorioProduct = product.id === 'solicitar_relatorio';
              const isRenainfProduct = product.id === 'consulta_renainf';
              const isFipeProduct = product.id === 'consulta_preco_fipe';
              const isProprietarioProduct = product.id === 'consulta_proprietario';
              const isLeilaoProduct = product.id === 'consulta_leilao_prime';
              const isSinistroPTProduct = product.id === 'consulta_sinistro_pt';
              
              let ProductIcon = Car;
              let gradientFrom = 'from-emerald-500/20';
              let gradientTo = 'to-teal-500/20';
              let borderColor = 'border-emerald-500/30';
              let iconColor = 'text-emerald-400';
              let bgIconColor = 'text-emerald-500';
              let hoverBorder = 'hover:border-emerald-500/50';
              let buttonGradient = 'from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 shadow-emerald-500/20';
              let dotColor = 'bg-emerald-500';
              let badgeText = 'Novo';
              let badgeColor = 'bg-emerald-500/20 text-emerald-400';

              if (isChassisProduct) {
                ProductIcon = Hash;
                gradientFrom = 'from-blue-500/20';
                gradientTo = 'to-cyan-500/20';
                borderColor = 'border-blue-500/30';
                iconColor = 'text-blue-400';
                bgIconColor = 'text-blue-500';
                hoverBorder = 'hover:border-blue-500/50';
                buttonGradient = 'from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 shadow-blue-500/20';
                dotColor = 'bg-blue-500';
                badgeColor = 'bg-blue-500/20 text-blue-400';
              } else if (isRenainfProduct) {
                ProductIcon = AlertTriangle;
                gradientFrom = 'from-amber-500/20';
                gradientTo = 'to-orange-500/20';
                borderColor = 'border-amber-500/30';
                iconColor = 'text-amber-400';
                bgIconColor = 'text-amber-500';
                hoverBorder = 'hover:border-amber-500/50';
                buttonGradient = 'from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500 shadow-amber-500/20';
                dotColor = 'bg-amber-500';
                badgeColor = 'bg-amber-500/20 text-amber-400';
              } else if (isFipeProduct) {
                ProductIcon = DollarSign;
                gradientFrom = 'from-green-500/20';
                gradientTo = 'to-emerald-500/20';
                borderColor = 'border-green-500/30';
                iconColor = 'text-green-400';
                bgIconColor = 'text-green-500';
                hoverBorder = 'hover:border-green-500/50';
                buttonGradient = 'from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 shadow-green-500/20';
                dotColor = 'bg-green-500';
                badgeColor = 'bg-green-500/20 text-green-400';
              } else if (isProprietarioProduct) {
                ProductIcon = UserIcon;
                gradientFrom = 'from-indigo-500/20';
                gradientTo = 'to-violet-500/20';
                borderColor = 'border-indigo-500/30';
                iconColor = 'text-indigo-400';
                bgIconColor = 'text-indigo-500';
                hoverBorder = 'hover:border-indigo-500/50';
                buttonGradient = 'from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500 shadow-indigo-500/20';
                dotColor = 'bg-indigo-500';
                badgeColor = 'bg-indigo-500/20 text-indigo-400';
              } else if (isLeilaoProduct) {
                ProductIcon = Gavel;
                gradientFrom = 'from-rose-500/20';
                gradientTo = 'to-red-500/20';
                borderColor = 'border-rose-500/30';
                iconColor = 'text-rose-400';
                bgIconColor = 'text-rose-500';
                hoverBorder = 'hover:border-rose-500/50';
                buttonGradient = 'from-rose-600 to-red-600 hover:from-rose-500 hover:to-red-500 shadow-rose-500/20';
                dotColor = 'bg-rose-500';
                badgeText = 'Premium';
                badgeColor = 'bg-rose-500/20 text-rose-400';
              } else if (isSinistroPTProduct) {
                ProductIcon = AlertOctagon;
                gradientFrom = 'from-orange-500/20';
                gradientTo = 'to-red-500/20';
                borderColor = 'border-orange-500/30';
                iconColor = 'text-orange-400';
                bgIconColor = 'text-orange-500';
                hoverBorder = 'hover:border-orange-500/50';
                buttonGradient = 'from-orange-600 to-red-600 hover:from-orange-500 hover:to-red-500 shadow-orange-500/20';
                dotColor = 'bg-orange-500';
                badgeColor = 'bg-orange-500/20 text-orange-400';
              } else if (isRelatorioProduct) {
                ProductIcon = FileText;
                gradientFrom = 'from-purple-500/20';
                gradientTo = 'to-pink-500/20';
                borderColor = 'border-purple-500/30';
                iconColor = 'text-purple-400';
                bgIconColor = 'text-purple-500';
                hoverBorder = 'hover:border-purple-500/50';
                buttonGradient = 'from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 shadow-purple-500/20';
                dotColor = 'bg-purple-500';
                badgeText = 'Premium';
                badgeColor = 'bg-purple-500/20 text-purple-400';
              }

              return (
              <div 
                key={product.id}
                className={`bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl border border-slate-700/50 p-6 ${hoverBorder} transition-all group relative overflow-hidden`}
              >
                {/* Background decoration */}
                <div className="absolute top-0 right-0 p-3 opacity-10 group-hover:opacity-20 transition-opacity">
                  <ProductIcon className={`w-28 h-28 ${bgIconColor}`} />
                </div>
                
                {/* Badge */}
                <span className={`absolute top-4 right-4 px-2 py-1 ${badgeColor} text-xs font-semibold rounded-full flex items-center gap-1`}>
                  <Sparkles className="w-3 h-3" />
                  {badgeText}
                </span>
                
                <div className="relative">
                  <div className={`w-12 h-12 bg-gradient-to-br ${gradientFrom} ${gradientTo} rounded-xl flex items-center justify-center mb-4 border ${borderColor}`}>
                    <ProductIcon className={`w-6 h-6 ${iconColor}`} />
                  </div>
                  
                  <h3 className="text-lg font-bold text-white mb-2">{product.name}</h3>
                  <p className="text-slate-400 text-sm mb-4 line-clamp-2">{product.description}</p>
                  
                  <ul className="space-y-2 mb-6">
                    {product.features.slice(0, 4).map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-slate-300">
                        <div className={`w-1.5 h-1.5 rounded-full ${dotColor}`}></div>
                        {feature}
                      </li>
                    ))}
                    {product.features.length > 4 && (
                      <li className="text-xs text-slate-500">
                        +{product.features.length - 4} mais...
                      </li>
                    )}
                  </ul>

                  <div className="flex items-center justify-between pt-4 border-t border-slate-700/50">
                    <span className="text-2xl font-bold text-white">
                      {product.creditCost} 
                      <span className="text-sm font-normal text-slate-500 ml-1">
                        crédito{product.creditCost > 1 ? 's' : ''}
                      </span>
                    </span>
                    <button 
                      onClick={() => handleConsultationProductClick(product)}
                      className={`px-4 py-2 bg-gradient-to-r ${buttonGradient} text-white rounded-lg text-sm font-medium transition-all shadow-lg`}
                    >
                      Consultar
                    </button>
                  </div>
                </div>
              </div>
            );
            })}
          </div>
        </div>
      )}

      {/* Recent History */}
      <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
        <h2 className="text-lg font-bold text-white flex items-center gap-2 mb-4">
          <Clock className="w-5 h-5 text-slate-400" />
          Histórico Recente
        </h2>
        
        <div className="space-y-3">
          {history.length === 0 ? (
            <div className="text-center py-8 text-slate-500 text-sm">
              Nenhuma consulta realizada ainda.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {history.map((record) => {
                // Check if it's a report (relatório)
                const isRelatorio = record.consultationType === 'solicitar_relatorio';
                const isConsultaBasica = record.consultationType === 'consulta_placa_basica' || record.consultationType === 'consulta_chassi_basica';
                
                if (isRelatorio) {
                  const situacao = (record.situacao || 'em_processamento') as SituacaoConsulta;
                  const tipoConsulta = (record.tipoConsulta || 'bronze') as TipoConsultaRelatorio;
                  const tipoInfo = TIPO_CONSULTA_INFO[tipoConsulta];
                  const situacaoInfo = SITUACAO_CONSULTA_INFO[situacao];
                  const placa = record.placa || record.plate || '???';
                  
                  const getStatusColors = () => {
                    switch (situacao) {
                      case 'finalizada':
                        return { bg: 'bg-emerald-400/10', text: 'text-emerald-400', border: 'border-emerald-500/30', hoverBorder: 'hover:border-emerald-500/50' };
                      case 'parcialmente_finalizada':
                        return { bg: 'bg-blue-400/10', text: 'text-blue-400', border: 'border-blue-500/30', hoverBorder: 'hover:border-blue-500/50' };
                      default:
                        return { bg: 'bg-amber-400/10', text: 'text-amber-400', border: 'border-amber-500/30', hoverBorder: 'hover:border-amber-500/50' };
                    }
                  };
                  
                  const StatusIcon = situacao === 'finalizada' ? CheckCircle : situacao === 'parcialmente_finalizada' ? Hourglass : Clock;
                  const colors = getStatusColors();
                  
                  return (
                    <div
                      key={record.id}
                      className={`p-4 bg-slate-950/50 rounded-lg border ${colors.border} ${colors.hoverBorder} transition-all`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-purple-400" />
                          <span className="font-mono font-bold text-white text-sm">{placa}</span>
                        </div>
                        <span className={`text-xs ${colors.bg} ${colors.text} px-2 py-1 rounded flex items-center gap-1`}>
                          <StatusIcon className={`w-3 h-3 ${situacao === 'em_processamento' ? 'animate-pulse' : ''}`} />
                          {situacaoInfo.label}
                        </span>
                      </div>
                      
                      <p className="text-xs text-slate-400 mb-3">
                        Relatório {tipoInfo?.name || tipoConsulta} • {record.creditsUsed} créditos
                      </p>
                      
                      <div className="flex gap-2">
                        {(situacao === 'finalizada' || situacao === 'parcialmente_finalizada') && record.urlPdf && (
                          <a
                            href={record.urlPdf}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 bg-emerald-600/20 text-emerald-400 rounded text-xs font-medium hover:bg-emerald-600/30 transition-colors"
                          >
                            <FileDown className="w-3 h-3" />
                            PDF
                          </a>
                        )}
                        <button
                          onClick={async () => {
                            if (record.protocolo) {
                              try {
                                const response = await consultarProtocolo(record.protocolo, 'PDF');
                                // Refresh history to update status
                                getConsultationHistory(10).then(setHistory);
                                // If finished, could show result
                                if (response.result.urlPdf) {
                                  window.open(response.result.urlPdf, '_blank');
                                }
                              } catch (err) {
                                console.error('Error checking protocol:', err);
                              }
                            }
                          }}
                          className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 bg-slate-700/50 text-slate-300 rounded text-xs font-medium hover:bg-slate-700 transition-colors"
                        >
                          <RefreshCw className="w-3 h-3" />
                          Atualizar
                        </button>
                      </div>
                    </div>
                  );
                }
                
                if (isConsultaBasica) {
                  // Handle consulta basica records
                  const result = record.result as unknown as ConsultaPlacaBasicaResult;
                  const placa = record.placa || record.plate || result?.placa || '???';
                  const marca = result?.dadosVeiculo?.marca || 'N/A';
                  const modelo = result?.dadosVeiculo?.modelo || 'N/A';
                  
                  return (
                    <button 
                      key={record.id}
                      onClick={() => {
                        if (result) {
                          onShowConsultaBasicaResult(result);
                        }
                      }}
                      className="p-3 bg-slate-950/50 rounded-lg border border-slate-800 flex items-center justify-between hover:border-slate-700 transition-all text-left"
                    >
                      <div className="flex items-center gap-3">
                        <Car className="w-4 h-4 text-emerald-400" />
                        <div>
                          <p className="font-mono font-bold text-white">{placa}</p>
                          <p className="text-xs text-slate-400">{marca} {modelo}</p>
                        </div>
                      </div>
                      <span className="text-xs text-emerald-400 bg-emerald-400/10 px-2 py-1 rounded flex items-center gap-1">
                        <ShieldCheck className="w-3 h-3" /> OK
                      </span>
                    </button>
                  );
                }
                
                // Default: old consultation records
                const vehicleResult = record.result as VehicleData;
                return (
                  <button 
                    key={record.id}
                    onClick={() => onShowResult(vehicleResult)}
                    className="p-3 bg-slate-950/50 rounded-lg border border-slate-800 flex items-center justify-between hover:border-slate-700 transition-all text-left"
                  >
                     <div>
                        <p className="font-mono font-bold text-white">{record.plate}</p>
                        <p className="text-xs text-slate-400">{vehicleResult?.make} {vehicleResult?.model}</p>
                     </div>
                     <div className="text-right">
                        {vehicleResult?.status === 'Regular' ? (
                          <span className="text-xs text-emerald-400 bg-emerald-400/10 px-2 py-1 rounded flex items-center gap-1">
                            <ShieldCheck className="w-3 h-3" /> OK
                          </span>
                        ) : (
                          <span className="text-xs text-red-400 bg-red-400/10 px-2 py-1 rounded flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" /> ALERTA
                          </span>
                        )}
                     </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <BuyCreditsModal 
        isOpen={showBuyCredits}
        onClose={() => setShowBuyCredits(false)}
        currentCredits={user.credits}
      />

      {/* Consulta Placa Básica Modal */}
      {selectedConsultationProduct && selectedConsultationProduct.id === 'consulta_placa_basica' && (
        <ConsultaPlacaBasicaModal
          isOpen={!!selectedConsultationProduct}
          onClose={() => setSelectedConsultationProduct(null)}
          product={selectedConsultationProduct}
          userCredits={user.credits}
          onSuccess={handleConsultaBasicaSuccess}
          onInsufficientCredits={handleInsufficientCredits}
        />
      )}

      {/* Consulta Chassi Básica Modal */}
      {selectedConsultationProduct && selectedConsultationProduct.id === 'consulta_chassi_basica' && (
        <ConsultaChassiBasicaModal
          isOpen={!!selectedConsultationProduct}
          onClose={() => setSelectedConsultationProduct(null)}
          product={selectedConsultationProduct}
          userCredits={user.credits}
          onSuccess={handleConsultaBasicaSuccess}
          onInsufficientCredits={handleInsufficientCredits}
        />
      )}

      {/* Solicitar Relatório Modal */}
      {selectedConsultationProduct && selectedConsultationProduct.id === 'solicitar_relatorio' && (
        <SolicitarRelatorioModal
          isOpen={!!selectedConsultationProduct}
          onClose={() => setSelectedConsultationProduct(null)}
          product={selectedConsultationProduct}
          userCredits={user.credits}
          onSuccess={handleRelatorioSuccess}
          onInsufficientCredits={handleInsufficientCredits}
        />
      )}

      {/* Consulta RENAINF Modal */}
      {selectedConsultationProduct && selectedConsultationProduct.id === 'consulta_renainf' && (
        <ConsultaRenainfModal
          isOpen={!!selectedConsultationProduct}
          onClose={() => setSelectedConsultationProduct(null)}
          product={selectedConsultationProduct}
          userCredits={user.credits}
          onSuccess={handleRenainfSuccess}
          onInsufficientCredits={handleInsufficientCredits}
        />
      )}

      {/* Consulta Preço FIPE Modal */}
      {selectedConsultationProduct && selectedConsultationProduct.id === 'consulta_preco_fipe' && (
        <ConsultaPrecoFipeModal
          isOpen={!!selectedConsultationProduct}
          onClose={() => setSelectedConsultationProduct(null)}
          product={selectedConsultationProduct}
          userCredits={user.credits}
          onSuccess={handleFipeSuccess}
          onInsufficientCredits={handleInsufficientCredits}
        />
      )}

      {/* Consulta Proprietário Modal */}
      {selectedConsultationProduct && selectedConsultationProduct.id === 'consulta_proprietario' && (
        <ConsultaProprietarioModal
          isOpen={!!selectedConsultationProduct}
          onClose={() => setSelectedConsultationProduct(null)}
          product={selectedConsultationProduct}
          userCredits={user.credits}
          onSuccess={handleProprietarioSuccess}
          onInsufficientCredits={handleInsufficientCredits}
        />
      )}

      {/* Consulta Leilão Prime Modal */}
      {selectedConsultationProduct && selectedConsultationProduct.id === 'consulta_leilao_prime' && (
        <ConsultaLeilaoPrimeModal
          isOpen={!!selectedConsultationProduct}
          onClose={() => setSelectedConsultationProduct(null)}
          product={selectedConsultationProduct}
          userCredits={user.credits}
          onSuccess={handleLeilaoSuccess}
          onInsufficientCredits={handleInsufficientCredits}
        />
      )}

      {/* Consulta Sinistro com Perda Total Modal */}
      {selectedConsultationProduct && selectedConsultationProduct.id === 'consulta_sinistro_pt' && (
        <ConsultaSinistroPTModal
          isOpen={!!selectedConsultationProduct}
          onClose={() => setSelectedConsultationProduct(null)}
          product={selectedConsultationProduct}
          userCredits={user.credits}
          onSuccess={handleSinistroPTSuccess}
          onInsufficientCredits={handleInsufficientCredits}
        />
      )}
    </div>
  );
};

export default Dashboard;
